import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Car, Globe } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { resolveImageUrl } from '../../utils/imageUrl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ADMIN_CARS_PER_PAGE = 10;

const AdminCars = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchCars();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCars = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/cars`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCars(response.data);
    } catch (error) {
      console.error('Error fetching cars:', error);
      toast.error('Erro ao carregar viaturas');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (carId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/cars/${carId}`, { status: 'Disponível' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Viatura publicada no site!');
      setCars(cars.map(c => c.id === carId ? { ...c, status: 'Disponível' } : c));
    } catch (error) {
      toast.error('Erro ao publicar viatura');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/cars/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Viatura eliminada com sucesso');
      setCars(cars.filter(c => c.id !== deleteId));
    } catch (error) {
      toast.error('Erro ao eliminar viatura');
    } finally {
      setDeleteId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Rascunho': 'bg-zinc-600/20 text-zinc-400 border border-zinc-600',
      'Disponível': 'badge-available',
      'Reservado': 'badge-reserved',
      'Vendido': 'badge-sold'
    };
    return styles[status] || styles['Rascunho'];
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('pt-PT').format(mileage);
  };

  const filteredCars = cars.filter(car => 
    `${car.brand} ${car.model}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredCars.length / ADMIN_CARS_PER_PAGE);
  const paginatedCars = filteredCars.slice((currentPage - 1) * ADMIN_CARS_PER_PAGE, currentPage * ADMIN_CARS_PER_PAGE);

  // Reset page when search changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-medium text-white mb-2" data-testid="cars-page-title">Gestão de Viaturas</h1>
            <p className="text-zinc-500">{cars.length} viatura(s) no total</p>
          </div>
          <Link
            to="/admin/viaturas/nova"
            className="bg-red-600 text-white font-semibold px-6 py-3 hover:bg-red-700 transition-colors inline-flex items-center gap-2 justify-center"
            data-testid="add-car-btn"
          >
            <Plus size={20} />
            Nova Viatura
          </Link>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            type="text"
            placeholder="Pesquisar por marca ou modelo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 bg-[#12141D] border-zinc-800 text-white placeholder:text-zinc-600 h-12"
            data-testid="search-cars-input"
          />
        </div>

        {/* Cars Table */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-zinc-400">A carregar viaturas...</p>
          </div>
        ) : filteredCars.length === 0 ? (
          <div className="text-center py-12 bg-[#12141D] border border-zinc-800">
            <Car size={48} className="mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">Nenhuma viatura encontrada</p>
            <Link
              to="/admin/viaturas/nova"
              className="text-red-500 hover:text-red-400 mt-2 inline-block"
            >
              Adicionar primeira viatura
            </Link>
          </div>
        ) : (
          <div className="bg-[#12141D] border border-zinc-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="cars-table">
                <thead className="border-b border-zinc-800">
                  <tr>
                    <th className="text-left text-zinc-500 text-xs font-semibold uppercase tracking-wider p-4">Viatura</th>
                    <th className="text-left text-zinc-500 text-xs font-semibold uppercase tracking-wider p-4">Ano</th>
                    <th className="text-left text-zinc-500 text-xs font-semibold uppercase tracking-wider p-4">Km</th>
                    <th className="text-left text-zinc-500 text-xs font-semibold uppercase tracking-wider p-4">Preço</th>
                    <th className="text-left text-zinc-500 text-xs font-semibold uppercase tracking-wider p-4">Estado</th>
                    <th className="text-right text-zinc-500 text-xs font-semibold uppercase tracking-wider p-4">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {paginatedCars.map((car) => (
                    <tr key={car.id} className="hover:bg-zinc-800/50 transition-colors" data-testid={`car-row-${car.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-12 bg-zinc-800 overflow-hidden flex-shrink-0">
                            <img 
                              src={resolveImageUrl(car.images?.[0]) || 'https://images.unsplash.com/photo-1761119278824-bcb65edd36ca?w=100'}
                              alt={`${car.brand} ${car.model}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <p className="text-white font-medium">{car.brand} {car.model}</p>
                            <p className="text-zinc-500 text-sm">{car.fuel} · {car.transmission}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-zinc-300">{car.year}</td>
                      <td className="p-4 text-zinc-300">{formatMileage(car.mileage)} km</td>
                      <td className="p-4 text-white font-medium">{formatPrice(car.price)}</td>
                      <td className="p-4">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadge(car.status)}`}>
                          {car.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {car.status === 'Rascunho' && (
                            <button
                              onClick={() => handlePublish(car.id)}
                              className="p-2 text-emerald-500 hover:text-emerald-400 hover:bg-zinc-700 transition-colors"
                              title="Publicar no site"
                              data-testid={`publish-car-${car.id}`}
                            >
                              <Globe size={18} />
                            </button>
                          )}
                          <Link
                            to={`/admin/viaturas/${car.id}`}
                            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
                            data-testid={`edit-car-${car.id}`}
                          >
                            <Pencil size={18} />
                          </Link>
                          <button
                            onClick={() => setDeleteId(car.id)}
                            className="p-2 text-zinc-400 hover:text-red-500 hover:bg-zinc-700 transition-colors"
                            data-testid={`delete-car-${car.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {filteredCars.length > ADMIN_CARS_PER_PAGE && (
          <div className="flex items-center justify-between mt-6" data-testid="admin-pagination">
            <p className="text-zinc-500 text-sm">
              A mostrar {(currentPage - 1) * ADMIN_CARS_PER_PAGE + 1}-{Math.min(currentPage * ADMIN_CARS_PER_PAGE, filteredCars.length)} de {filteredCars.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="admin-pagination-prev"
              >
                Anterior
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 text-sm font-medium transition-colors ${
                    page === currentPage ? 'bg-red-600 text-white' : 'border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600'
                  }`}
                  data-testid={`admin-pagination-page-${page}`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="admin-pagination-next"
              >
                Seguinte
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent className="bg-[#12141D] border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Eliminar Viatura</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Tem a certeza que deseja eliminar esta viatura? Esta ação não pode ser revertida.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-500 text-white hover:bg-red-600"
                data-testid="confirm-delete-btn"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminCars;
